import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { findWeatherById } from '../data/weatherOptions';
import {
  DYNAMIC_WEATHER_BLEND_SECONDS,
  DYNAMIC_WEATHER_HOLD_SECONDS,
  DYNAMIC_WEATHER_SEQUENCE,
  mixRuntimeWeatherInto,
  toRuntimeWeather,
  type RuntimeWeather,
} from '../model/weatherTransition';
import type { WeatherOption } from '../types';

/**
 * 동적 날씨 진행.
 *
 * 보간 값을 React state로 올리지 않습니다. setState를 프레임 루프에서 부르면
 * DrivingScene 아래의 노면·표식·강수·환경맵이 초당 수십 번 재조정되고, 절차적 배경에서는
 * drei Environment가 children 정체성 변화를 보고 큐브맵을 다시 굽습니다.
 *
 * 그래서 물리 쪽(surfaceRef)과 같은 방식을 씁니다 — **식별자를 고정한 객체 하나**를 두고
 * 필드만 덮어씁니다. 소비자들은 매 프레임 이 객체를 읽어 재질·안개·광량에 직접 씁니다.
 */
export function useRuntimeWeatherRef(
  selected: WeatherOption,
  dynamic: boolean
): RuntimeWeather {
  const fixed = useMemo(() => toRuntimeWeather(selected), [selected]);
  const runtime = useRef<RuntimeWeather>({ ...fixed });
  const progress = useRef({ elapsed: 0, index: 0 });
  const scratch = useRef({
    from: toRuntimeWeather(selected),
    to: toRuntimeWeather(selected),
  });

  /*
   * 고정 날씨이거나 프리셋이 바뀌면 즉시 반영합니다. 렌더 중에 써도 되는 이유는
   * 이 객체가 React 트리 밖의 가변 상태이고, 소비자가 프레임 루프에서 다시 읽기 때문입니다.
   */
  if (!dynamic) {
    Object.assign(runtime.current, fixed);
  }

  useEffect(() => {
    progress.current = { elapsed: 0, index: 0 };
    if (!dynamic) {
      Object.assign(runtime.current, fixed);
    }
  }, [dynamic, fixed]);

  useFrame((_, delta) => {
    if (!dynamic) {
      return;
    }
    const step = Math.min(Math.max(delta, 0), 1 / 20);
    const cycleLength = DYNAMIC_WEATHER_HOLD_SECONDS + DYNAMIC_WEATHER_BLEND_SECONDS;
    const tracker = progress.current;
    tracker.elapsed += step;
    if (tracker.elapsed >= cycleLength) {
      tracker.elapsed -= cycleLength;
      tracker.index = (tracker.index + 1) % DYNAMIC_WEATHER_SEQUENCE.length;
    }

    const from = findWeatherById(DYNAMIC_WEATHER_SEQUENCE[tracker.index]);
    const to = findWeatherById(
      DYNAMIC_WEATHER_SEQUENCE[(tracker.index + 1) % DYNAMIC_WEATHER_SEQUENCE.length]
    );
    if (from === undefined || to === undefined) {
      return;
    }

    // 프리셋 → 런타임 변환도 기존 객체에 덮어써서 프레임마다 할당하지 않습니다.
    toRuntimeWeather(from, scratch.current.from);
    toRuntimeWeather(to, scratch.current.to);
    const blend =
      tracker.elapsed <= DYNAMIC_WEATHER_HOLD_SECONDS
        ? 0
        : (tracker.elapsed - DYNAMIC_WEATHER_HOLD_SECONDS) / DYNAMIC_WEATHER_BLEND_SECONDS;
    mixRuntimeWeatherInto(scratch.current.from, scratch.current.to, blend, runtime.current);
  });

  return runtime.current;
}
